/**
 * Gallery controller — renders the category tab bar and gallery grid,
 * handles tab switching (with a fade transition) and expanding
 * a tile in place (no overlay/modal) to reveal its details and, when
 * available, a small embedded 3D model viewer.
 *
 * URL state: the active category is reflected in the `?category=` query
 * param so a specific view is linkable and survives a refresh.
 */

import { marked } from 'marked';
import { categoryDefinitions, categoryOrder, CategoryType, defaultCategory, isCategoryType, alwaysExpandedCategories } from './categories';
import { DEFAULT_MODEL_CANVAS_HEIGHT_PERCENT, galleryItems, GalleryItemDef } from './galleryData';
import { ModelViewer } from './modelViewer';
import { profile } from './profileData';
import { CLOSE_SVG } from './icons';

const CATEGORY_PARAM = 'category';
const TRANSITION_MS = 266;
const PHOTO_FADE_MS = 280;
const EDUCATION_LAYOUT_BUFFER_PX = 12;

const SHUFFLE_SVG = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="4" y1="4" x2="21" y2="21"/></svg>';

export class Gallery {
  private navEl: HTMLElement;
  private gridEl: HTMLElement;

  private activeCategory: CategoryType;
  private tabButtons: Map<CategoryType, HTMLButtonElement> = new Map();
  private modelViewer: ModelViewer | null = null;
  private expandedItemId: string | null = null;
  private isBusy = false;
  private photoModalEl: HTMLElement | null = null;
  private photoModalKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  private photoModalTrigger: HTMLButtonElement | null = null;
  private blogModalEl: HTMLElement | null = null;
  private blogModalKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  private blogModalTrigger: HTMLButtonElement | null = null;
  private educationResizeObserver: ResizeObserver | null = null;
  private educationLayoutFrame: number | null = null;
  /** Remembers the current photo shuffle order (item ids) for the session so it survives switching away and back to the photography tab. Not persisted across page reloads. */
  private photoOrder: string[] | null = null;
  /** Subcategories currently shown in the photography grid. Multiple can be active at once; null means "not yet initialized" (defaults to all shown). */
  private activeSubcategories: Set<string> | null = null;

  constructor() {
    this.navEl = this.requireEl('category-nav');
    this.gridEl = this.requireEl('gallery-grid');

    this.activeCategory = this.readCategoryFromUrl() ?? defaultCategory;

    this.buildTabs();
    this.renderGrid(this.activeCategory);
    this.setActiveTab(this.activeCategory);
    this.syncUrl(this.activeCategory, true);

    document.addEventListener('keydown', (e) => this.onDocumentKeydown(e));
    window.addEventListener('popstate', () => {
      const category = this.readCategoryFromUrl() ?? defaultCategory;
      if (category !== this.activeCategory) this.switchCategory(category, false);
    });
  }

  private requireEl(id: string): HTMLElement {
    const el = document.getElementById(id);
    if (!el) throw new Error(`Missing required element #${id}`);
    return el;
  }

  private readCategoryFromUrl(): CategoryType | null {
    const params = new URLSearchParams(window.location.search);
    return isCategoryType(params.get(CATEGORY_PARAM)) ? (params.get(CATEGORY_PARAM) as CategoryType) : null;
  }

  private syncUrl(category: CategoryType, replace: boolean): void {
    const url = new URL(window.location.href);
    url.searchParams.set(CATEGORY_PARAM, category);
    if (replace) {
      window.history.replaceState(null, '', url);
    } else {
      window.history.pushState(null, '', url);
    }
  }

  // ============================================================================
  // Tabs
  // ============================================================================

  private buildTabs(): void {
    categoryDefinitions.forEach((category, index) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'category-tab';
      btn.id = `tab-${category.id}`;
      btn.textContent = category.label;
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-controls', 'gallery-grid');
      btn.setAttribute('aria-selected', 'false');
      btn.tabIndex = -1;
      btn.addEventListener('click', () => this.selectCategory(category.id));
      btn.addEventListener('keydown', (e) => this.onTabKeydown(e, index));
      this.navEl.appendChild(btn);
      this.tabButtons.set(category.id, btn);
    });
  }

  private onTabKeydown(e: KeyboardEvent, index: number): void {
    let nextIndex: number | null = null;
    if (e.key === 'ArrowRight') nextIndex = (index + 1) % categoryOrder.length;
    else if (e.key === 'ArrowLeft') nextIndex = (index - 1 + categoryOrder.length) % categoryOrder.length;
    else if (e.key === 'Home') nextIndex = 0;
    else if (e.key === 'End') nextIndex = categoryOrder.length - 1;

    if (nextIndex !== null) {
      e.preventDefault();
      const category = categoryOrder[nextIndex];
      this.selectCategory(category);
      this.tabButtons.get(category)?.focus();
    }
  }

  private setActiveTab(category: CategoryType): void {
    this.tabButtons.forEach((btn, id) => {
      const isActive = id === category;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
      btn.tabIndex = isActive ? 0 : -1;
    });
    this.gridEl.setAttribute('aria-labelledby', `tab-${category}`);
  }

  private selectCategory(category: CategoryType): void {
    if (category === this.activeCategory || this.isBusy) return;
    this.closePhotoModal();
    this.closeBlogModal();
    const expandedTiles = Array.from(this.gridEl.querySelectorAll('.gallery-item.expanded')) as HTMLElement[];
    expandedTiles.forEach(tile => this.collapseItem(tile));
    this.switchCategory(category, true);
  }

  private switchCategory(category: CategoryType, pushUrl: boolean): void {
    this.isBusy = true;
    this.activeCategory = category;
    this.setActiveTab(category);
    this.syncUrl(category, !pushUrl);

    // Fade the current grid out, swap its contents once invisible, then fade
    // the new content back in.
    this.gridEl.style.transition = `opacity ${TRANSITION_MS}ms var(--transition-easing)`;
    this.gridEl.style.opacity = '0';

    window.setTimeout(() => {
      this.renderGrid(category);
      void this.gridEl.offsetHeight; // force reflow so the fade-in transition runs

      this.gridEl.style.transition = `opacity ${TRANSITION_MS}ms var(--transition-easing)`;
      this.gridEl.style.opacity = '1';

      window.setTimeout(() => {
        this.gridEl.style.transition = '';
        this.gridEl.style.opacity = '';
        this.isBusy = false;
      }, TRANSITION_MS);
    }, TRANSITION_MS);
  }

  // ============================================================================
  // Grid
  // ============================================================================

  private renderGrid(category: CategoryType): void {
    this.expandedItemId = null;
    this.closePhotoModal();
    this.closeBlogModal();
    this.modelViewer?.dispose();
    this.modelViewer = null;

    this.educationResizeObserver?.disconnect();
    this.educationResizeObserver = null;
    if (this.educationLayoutFrame !== null) {
      cancelAnimationFrame(this.educationLayoutFrame);
      this.educationLayoutFrame = null;
    }

    this.gridEl.innerHTML = '';
    this.gridEl.classList.toggle('gallery-grid--photo', category === 'photography');
    this.gridEl.classList.toggle('gallery-grid--about', category === 'about');
    this.gridEl.classList.toggle('gallery-grid--blog', category === 'blog');
    const useStackedColumns = category !== 'about' && category !== 'photography';
    this.gridEl.classList.toggle('gallery-grid--stacked-columns', useStackedColumns);
    if (category === 'about') {
      this.gridEl.appendChild(this.createAboutPanel());
      return;
    }
    if (category === 'photography') {
      this.gridEl.appendChild(this.createPhotoToolbar());
    }
    const items = galleryItems.filter((item) => item.category === category);
    if (category === 'photography' && this.photoOrder) {
      const byId = new Map(items.map((item) => [item.id, item]));
      const ordered: GalleryItemDef[] = [];
      for (const id of this.photoOrder) {
        const item = byId.get(id);
        if (item) {
          ordered.push(item);
          byId.delete(id);
        }
      }
      // Append any items not present in the stored order (e.g. newly added photos).
      ordered.push(...byId.values());
      items.length = 0;
      items.push(...ordered);
    }
    const isStatic = alwaysExpandedCategories.has(category);
    const useTwoColumnStacks = useStackedColumns && window.matchMedia('(min-width: 721px)').matches;
    if (useTwoColumnStacks) {
      this.renderInStackedColumns(items, isStatic);
    } else {
      items.forEach((item) => this.appendTile(item, isStatic, this.gridEl));
    }
    if (category === 'photography') {
      this.applySubcategoryFilter();
    }
  }

  /**
   * Render two explicit columns so item 1/2, 3/4, ... always start side-by-side
   * regardless of card height differences.
   */
  private renderInStackedColumns(items: GalleryItemDef[], isStatic: boolean): void {
    const leftColumn = document.createElement('div');
    leftColumn.className = 'gallery-stack-column gallery-stack-column--left';

    const rightColumn = document.createElement('div');
    rightColumn.className = 'gallery-stack-column gallery-stack-column--right';

    items.forEach((item, index) => {
      const targetColumn = index % 2 === 0 ? leftColumn : rightColumn;
      this.appendTile(item, isStatic, targetColumn);
    });

    this.gridEl.append(leftColumn, rightColumn);
  }

  private appendTile(item: GalleryItemDef, isStatic: boolean, parent: HTMLElement): void {
    const tile = isStatic ? this.createStaticTile(item) : this.createTile(item);
    parent.appendChild(tile);

    // Model viewer must be initialised after the tile is in the DOM.
    if (!isStatic || !item.model) {
      return;
    }

    const modelMount = tile.querySelector<HTMLElement>('.gallery-item-model-mount');
    if (!modelMount) {
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.className = 'model-canvas';
    modelMount.appendChild(canvas);
    this.modelViewer = new ModelViewer(canvas);
    this.modelViewer.loadFromDef(item.model).catch((error) => {
      console.error(`Failed to load model for ${item.id}:`, error);
    });
  }

  /** 'About' has no gallery items — just a short personal summary and education info. */
  private createAboutPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'about-panel';

    const intro = document.createElement('div');
    intro.className = 'about-panel-intro';

    const photo = document.createElement('div');
    photo.className = 'about-panel-photo';
    if (profile.aboutPhotoSrc) {
      photo.style.backgroundImage = `url("${profile.aboutPhotoSrc}")`;
      photo.style.backgroundSize = 'cover';
      photo.style.backgroundPosition = 'center';
    }
    intro.appendChild(photo);

    const textBlock = document.createElement('div');
    textBlock.className = 'about-panel-text';

    const aboutHeading = document.createElement('h3');
    aboutHeading.className = 'about-panel-subheading';
    aboutHeading.textContent = 'About Me';
    textBlock.appendChild(aboutHeading);

    const summary = document.createElement('div');
    summary.className = 'about-panel-summary';
    summary.style.setProperty('--about-me-paragraph-gap', profile.aboutMe.paragraphSpacing ?? '0.85rem');
    for (const paragraph of profile.aboutMe.paragraphs) {
      const paragraphEl = document.createElement('p');
      paragraphEl.textContent = paragraph;
      summary.appendChild(paragraphEl);
    }
    textBlock.appendChild(summary);

    const eduHeading = document.createElement('h3');
    eduHeading.className = 'about-panel-subheading';
    eduHeading.textContent = 'Education';
    textBlock.appendChild(eduHeading);

    const eduList = document.createElement('ul');
    eduList.className = 'about-panel-education-list';
    for (const entry of profile.education) {
      const li = document.createElement('li');
      const degree = document.createElement('strong');
      degree.className = 'about-panel-education-degree';
      degree.textContent = entry.degree;
      const sepDegree = document.createElement('span');
      sepDegree.className = 'about-panel-education-separator about-panel-education-separator-degree';
      sepDegree.setAttribute('aria-hidden', 'true');
      sepDegree.textContent = '  ·  ';
      const institution = document.createElement('span');
      institution.className = 'about-panel-education-institution';
      institution.textContent = entry.institution;
      const sepInstitution = document.createElement('span');
      sepInstitution.className = 'about-panel-education-separator about-panel-education-separator-institution';
      sepInstitution.setAttribute('aria-hidden', 'true');
      sepInstitution.textContent = '  ·  ';
      const period = document.createElement('span');
      period.className = 'about-panel-education-period';
      period.textContent = entry.period;
      li.append(degree, sepDegree, institution, sepInstitution, period);
      eduList.appendChild(li);
    }
    textBlock.appendChild(eduList);

    intro.appendChild(textBlock);
    panel.appendChild(intro);

    this.observeEducationLayout(eduList);

    return panel;
  }

  /**
   * Education entries have three fixed responsive layouts:
   * 1 line: degree · institution · period
   * 2 lines: degree / institution · period
   * 3 lines: degree / institution / period
   * Measure the candidate lines on resize so entries never fall into arbitrary
   * browser wraps such as degree + institution / period.
   */
  private observeEducationLayout(eduList: HTMLElement): void {
    const update = () => {
      this.educationLayoutFrame = null;
      if (eduList.isConnected) this.updateEducationLayouts(eduList);
    };
    const scheduleUpdate = () => {
      if (this.educationLayoutFrame !== null) return;
      this.educationLayoutFrame = requestAnimationFrame(update);
    };
    this.educationResizeObserver = new ResizeObserver(scheduleUpdate);
    this.educationResizeObserver.observe(eduList);
    scheduleUpdate();
  }

  private updateEducationLayouts(eduList: HTMLElement): void {
    const items = Array.from(eduList.children) as HTMLLIElement[];
    for (const li of items) {
      const degree = li.querySelector<HTMLElement>('.about-panel-education-degree');
      const institution = li.querySelector<HTMLElement>('.about-panel-education-institution');
      const period = li.querySelector<HTMLElement>('.about-panel-education-period');
      const [sepDegree, sepInstitution] = li.querySelectorAll<HTMLElement>('.about-panel-education-separator');
      if (!degree || !institution || !period || !sepDegree || !sepInstitution) continue;

      const availableWidth = li.clientWidth - EDUCATION_LAYOUT_BUFFER_PX;
      const singleLineWidth = this.measureEducationLine(li, [degree, sepDegree, institution, sepInstitution, period]);
      const secondLineWidth = this.measureEducationLine(li, [institution, sepInstitution, period]);

      li.dataset.layout = singleLineWidth <= availableWidth
        ? '1-line'
        : secondLineWidth <= availableWidth
          ? '2-line'
          : '3-line';
    }
  }

  private measureEducationLine(container: HTMLElement, parts: HTMLElement[]): number {
    const measurer = document.createElement('span');
    measurer.className = 'about-panel-education-measure';
    for (const part of parts) {
      const clone = part.cloneNode(true) as HTMLElement;
      clone.style.display = 'inline';
      clone.style.whiteSpace = 'nowrap';
      measurer.appendChild(clone);
    }
    container.appendChild(measurer);
    const width = measurer.getBoundingClientRect().width;
    measurer.remove();
    return width;
  }

  private createPhotoToolbar(): HTMLElement {
    const toolbar = document.createElement('div');
    toolbar.className = 'gallery-photo-toolbar';

    const shuffleBtn = document.createElement('button');
    shuffleBtn.type = 'button';
    shuffleBtn.className = 'gallery-photo-shuffle-btn';
    shuffleBtn.setAttribute('aria-label', 'Shuffle photos');
    shuffleBtn.innerHTML = `${SHUFFLE_SVG}<span>Shuffle</span>`;
    shuffleBtn.addEventListener('click', () => this.shufflePhotos());

    toolbar.appendChild(shuffleBtn);

    const subcategories = this.getPhotoSubcategories();
    if (this.activeSubcategories === null) {
      this.activeSubcategories = new Set(subcategories);
    }
    for (const subcategory of subcategories) {
      toolbar.appendChild(this.createSubcategoryButton(subcategory));
    }

    return toolbar;
  }

  /** Unique subcategory values across all photography items, in first-appearance order. */
  private getPhotoSubcategories(): string[] {
    const seen = new Set<string>();
    const subcategories: string[] = [];
    for (const item of galleryItems) {
      if (item.category !== 'photography' || !item.subcategory) continue;
      if (!seen.has(item.subcategory)) {
        seen.add(item.subcategory);
        subcategories.push(item.subcategory);
      }
    }
    return subcategories;
  }

  private createSubcategoryButton(subcategory: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'gallery-photo-shuffle-btn gallery-photo-subcategory-btn';
    const isActive = this.activeSubcategories?.has(subcategory) ?? true;
    btn.classList.toggle('active', isActive);
    btn.setAttribute('aria-pressed', String(isActive));
    btn.setAttribute('aria-label', `Toggle ${subcategory} photos`);
    btn.textContent = subcategory;
    btn.addEventListener('click', () => this.toggleSubcategory(subcategory, btn));
    return btn;
  }

  private toggleSubcategory(subcategory: string, btn: HTMLButtonElement): void {
    if (!this.activeSubcategories) return;
    const isActive = !this.activeSubcategories.has(subcategory);
    if (isActive) {
      this.activeSubcategories.add(subcategory);
    } else {
      this.activeSubcategories.delete(subcategory);
    }
    btn.classList.toggle('active', isActive);
    btn.setAttribute('aria-pressed', String(isActive));
    this.applySubcategoryFilter();
  }

  /** Shows/hides photo tiles based on this.activeSubcategories. */
  private applySubcategoryFilter(): void {
    if (!this.activeSubcategories) return;
    const tiles = Array.from(this.gridEl.children).filter(
      (el) => el.classList.contains('gallery-item-photo')
    ) as HTMLElement[];
    for (const tile of tiles) {
      const subcategory = tile.dataset.subcategory;
      const visible = !subcategory || this.activeSubcategories.has(subcategory);
      tile.classList.toggle('gallery-item-photo-hidden', !visible);
    }
  }

  private shufflePhotos(): void {
    const tiles = Array.from(this.gridEl.children).filter(
      (el) => el.classList.contains('gallery-item-photo')
    ) as HTMLElement[];

    if (tiles.length === 0) return;

    // Fade out
    for (const tile of tiles) {
      tile.style.transition = `opacity ${PHOTO_FADE_MS}ms var(--transition-easing)`;
      tile.style.opacity = '0';
    }

    setTimeout(() => {
      // Fisher-Yates shuffle
      for (let i = tiles.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
      }
      // Re-append in shuffled order; toolbar stays as first child
      for (const tile of tiles) {
        this.gridEl.appendChild(tile);
      }
      this.photoOrder = tiles.map((tile) => tile.dataset.itemId!).filter(Boolean);
      // Double RAF ensures the opacity:0 is painted before we set it to 1
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          for (const tile of tiles) {
            tile.style.opacity = '1';
          }
          setTimeout(() => {
            for (const tile of tiles) {
              tile.style.transition = '';
              tile.style.opacity = '';
            }
          }, PHOTO_FADE_MS);
        });
      });
    }, PHOTO_FADE_MS);
  }

  /** Creates a title element that is a link with an up-right arrow if a link exists, otherwise a plain heading. */
  private createTitleEl(item: GalleryItemDef, tagName: 'h2' | 'h3', variant: 'caption' | 'details'): HTMLElement {
    const UP_RIGHT_ARROW_SVG = '<svg width="1.15em" height="1.15em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="title-link-arrow"><line x1="7" y1="17" x2="17" y2="7"></line><polyline points="7 7 17 7 17 17"></polyline></svg>';

    if (item.link) {
      const linkEl = document.createElement('a');
      linkEl.href = item.link;
      linkEl.target = '_blank';
      linkEl.rel = 'noopener noreferrer';
      linkEl.className = variant === 'caption' ? 'gallery-item-title-link-caption' : 'gallery-item-title-link-details';
      linkEl.setAttribute('aria-label', `${item.title} (opens in new window)`);
      linkEl.addEventListener('click', (e) => e.stopPropagation());
      
      const titleEl = document.createElement(tagName);
      titleEl.className = 'gallery-item-title';
      titleEl.innerHTML = `${item.title}${UP_RIGHT_ARROW_SVG}`;
      linkEl.append(titleEl);
      return linkEl;
    } else {
      const titleEl = document.createElement(tagName);
      titleEl.className = 'gallery-item-title';
      titleEl.textContent = item.title;
      return titleEl;
    }
  }

  private createTile(item: GalleryItemDef): HTMLElement {
    if (item.category === 'photography') return this.createPhotoTile(item);
    if (item.category === 'blog') return this.createBlogTile(item);

    const tile = document.createElement('div');
    tile.className = 'gallery-item';
    tile.dataset.itemId = item.id;
    this.applyModelCanvasStyleVars(tile, item);

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'gallery-item-toggle';
    toggle.setAttribute('aria-expanded', 'false');

    const media = document.createElement('div');
    media.className = 'gallery-item-media';
    this.applyTileBackground(media, item);
    if (item.imageAlt) {
      media.setAttribute('role', 'img');
      media.setAttribute('aria-label', item.imageAlt);
    }

    const caption = document.createElement('div');
    caption.className = 'gallery-item-caption';

    caption.append(this.createTitleEl(item, 'h3', 'caption'));

    const desc = document.createElement('p');
    desc.textContent = item.description;
    caption.append(desc);

    toggle.append(media, caption);
    toggle.addEventListener('click', () => this.toggleItem(item, tile));

    tile.append(toggle);
    return tile;
  }

  private applyModelCanvasStyleVars(tile: HTMLElement, item: GalleryItemDef): void {
    const rawHeight = item.modelCanvasHeightPercent;
    const percent = Number.isFinite(rawHeight)
      ? Math.max(0, Math.min(100, Number(rawHeight)))
      : DEFAULT_MODEL_CANVAS_HEIGHT_PERCENT;
    tile.style.setProperty('--model-canvas-height-percent', String(percent));
  }

  /** CSS background for a tile: an image layered over its gradient, or the gradient alone. */
  /** Extract gradient variable name from "var(--tile-grad-X)" format */
  private extractGradientNumber(gradient: string): string {
    const match = gradient.match(/--tile-grad-(\d+)/);
    return match ? match[1] : '1';
  }

  /** Set gradient data attribute and background image if present */
  private applyTileBackground(media: HTMLElement, item: GalleryItemDef): void {
    const gradient = item.gradient ?? 'var(--tile-grad-1)';
    const gradientNum = this.extractGradientNumber(gradient);
    media.dataset.gradient = gradientNum;
    
    if (item.imageSrc) {
      media.style.backgroundImage = `url("${item.imageSrc}")`;
      media.style.backgroundPosition = 'center';
      media.style.backgroundSize = 'cover';
      media.style.backgroundRepeat = 'no-repeat';
    }
  }

  /** Photography tile: full-bleed image, no overlay text — click opens a full-screen modal. */
  private createPhotoTile(item: GalleryItemDef): HTMLElement {
    const tile = document.createElement('div');
    tile.className = 'gallery-item gallery-item-photo';
    tile.dataset.itemId = item.id;
    if (item.subcategory) tile.dataset.subcategory = item.subcategory;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'gallery-item-toggle';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', item.imageAlt ?? item.title);

    const media = document.createElement('div');
    media.className = 'gallery-item-media';
    this.applyTileBackground(media, item);

    toggle.append(media);
    toggle.addEventListener('click', () => this.openPhotoModal(item, toggle));

    // Detect actual image aspect ratio; assign grid-column/grid-row spans so every
    // tile's long side = N cells (N×40px + (N-1)×8px = 280px).
    if (item.imageSrc) {
      const N = 6;
      const applyRatio = (w: number, h: number) => {
        const ratio = w / h;
        tile.style.setProperty('--aspect-ratio', String(ratio));
        if (ratio >= 1) {
          // landscape: long side is width
          tile.style.gridColumn = `span ${N}`;
          tile.style.gridRow = `span ${Math.max(1, Math.round(N / ratio))}`;
        } else {
          // portrait: long side is height
          tile.style.gridColumn = `span ${Math.max(1, Math.round(N * ratio))}`;
          tile.style.gridRow = `span ${N}`;
        }
      };
      const probe = new Image();
      probe.addEventListener('load', () => applyRatio(probe.naturalWidth, probe.naturalHeight), { once: true });
      probe.src = item.imageSrc;
      if (probe.complete && probe.naturalWidth > 0) applyRatio(probe.naturalWidth, probe.naturalHeight);
    }

    tile.append(toggle);
    return tile;
  }

  private openPhotoModal(item: GalleryItemDef, trigger: HTMLButtonElement): void {
    if (this.photoModalEl) this.closePhotoModal();

    const overlay = document.createElement('div');
    overlay.className = 'photo-modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', item.title);

    const modal = document.createElement('div');
    modal.className = 'photo-modal';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'photo-modal-close';
    closeBtn.setAttribute('aria-label', 'Close photo');
    closeBtn.innerHTML = CLOSE_SVG;

    const img = document.createElement('img');
    img.className = 'photo-modal-image';
    img.src = item.imageSrc ?? '';
    img.alt = item.imageAlt ?? item.title;
    img.draggable = false;

    const info = document.createElement('div');
    info.className = 'photo-modal-info';

    const titleEl = document.createElement('h2');
    titleEl.className = 'photo-modal-title';
    titleEl.textContent = item.title;
    info.append(titleEl);

    if (item.description) {
      const desc = document.createElement('p');
      desc.className = 'photo-modal-description';
      desc.textContent = item.description;
      info.append(desc);
    }

    if (item.photoInfo) {
      const parts: string[] = [];
      if (item.photoInfo.location) parts.push(item.photoInfo.location);
      if (item.photoInfo.date) parts.push(item.photoInfo.date);
      if (item.photoInfo.camera) parts.push(item.photoInfo.camera);
      if (item.photoInfo.film) parts.push(item.photoInfo.film);
      if (parts.length > 0) {
        const meta = document.createElement('p');
        meta.className = 'photo-modal-meta';
        parts.forEach((part, i) => {
          if (i > 0) {
            const sep = document.createElement('span');
            sep.className = 'photo-meta-sep';
            sep.textContent = '  ·  ';
            meta.append(sep);
          }
          const field = document.createElement('span');
          field.className = 'photo-meta-item';
          field.textContent = part;
          meta.append(field);
        });
        info.append(meta);
      }
    }

    modal.append(img, info);
    overlay.append(closeBtn, modal);
    document.body.append(overlay);

    this.photoModalEl = overlay;
    this.photoModalTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');

    // Determine landscape vs portrait layout once image dimensions are known
    const applyLayout = () => {
      overlay.dataset.layout = img.naturalWidth > img.naturalHeight ? 'landscape' : 'portrait';
    };
    if (img.complete && img.naturalWidth > 0) {
      applyLayout();
    } else {
      img.addEventListener('load', applyLayout, { once: true });
    }

    // Prevent body scroll while modal is open (scrollbar-gutter: stable on <html>
    // keeps the gutter reserved so no layout shift occurs)
    document.documentElement.style.overflow = 'hidden';

    // Animate in on the next frame so the opacity transition fires
    requestAnimationFrame(() => overlay.classList.add('visible'));

    // Close on overlay backdrop click, close button, or Escape. The info
    // column's own background is the same blurred backdrop, so clicking its
    // empty space (not the title/description/meta text) also closes it.
    closeBtn.addEventListener('click', () => this.closePhotoModal());
    overlay.addEventListener('click', (e) => { if (e.target === overlay || e.target === info) this.closePhotoModal(); });

    this.photoModalKeyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.closePhotoModal();
    };
    document.addEventListener('keydown', this.photoModalKeyHandler);

    requestAnimationFrame(() => closeBtn.focus());
  }

  private closePhotoModal(): void {
    if (!this.photoModalEl) return;
    const overlay = this.photoModalEl;
    this.photoModalEl = null;

    if (this.photoModalKeyHandler) {
      document.removeEventListener('keydown', this.photoModalKeyHandler);
      this.photoModalKeyHandler = null;
    }

    document.documentElement.style.overflow = '';

    this.photoModalTrigger?.setAttribute('aria-expanded', 'false');
    this.photoModalTrigger?.focus();
    this.photoModalTrigger = null;

    overlay.classList.remove('visible');
    overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
    window.setTimeout(() => overlay.remove(), TRANSITION_MS + 50);
  }

  /** Blog tile: standard card layout (media + title/description); click opens the markdown overlay. */
  private createBlogTile(item: GalleryItemDef): HTMLElement {
    const tile = document.createElement('div');
    tile.className = 'gallery-item gallery-item-blog';
    tile.dataset.itemId = item.id;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'gallery-item-toggle';
    toggle.setAttribute('aria-haspopup', 'dialog');
    toggle.setAttribute('aria-expanded', 'false');

    const media = document.createElement('div');
    media.className = 'gallery-item-media';
    this.applyTileBackground(media, item);
    if (item.imageAlt) {
      media.setAttribute('role', 'img');
      media.setAttribute('aria-label', item.imageAlt);
    }

    const caption = document.createElement('div');
    caption.className = 'gallery-item-caption';
    caption.append(this.createTitleEl(item, 'h3', 'caption'));

    const desc = document.createElement('p');
    desc.textContent = item.description;
    caption.append(desc);

    toggle.append(media, caption);
    toggle.addEventListener('click', () => this.openBlogModal(item, toggle));

    tile.append(toggle);
    return tile;
  }

  private openBlogModal(item: GalleryItemDef, trigger: HTMLButtonElement): void {
    if (this.blogModalEl) this.closeBlogModal();
    if (!item.markdownPath) return;

    const overlay = document.createElement('div');
    overlay.className = 'blog-modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', item.title);

    const modal = document.createElement('div');
    modal.className = 'blog-modal';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'blog-modal-close';
    closeBtn.setAttribute('aria-label', 'Close post');
    closeBtn.innerHTML = CLOSE_SVG;

    // Card details (title + date/description) positioned within the reserved
    // padding above the markdown content — sits outside the scrollable
    // article so it never shifts the markdown content down. The title row
    // is a fixed height matching the close button so the two stay vertically
    // centered on each other.
    const meta = document.createElement('div');
    meta.className = 'blog-modal-meta';
    const titleRow = document.createElement('div');
    titleRow.className = 'blog-modal-meta-title-row';
    titleRow.append(this.createTitleEl(item, 'h2', 'details'));
    meta.append(titleRow);
    if (item.details) {
      const date = document.createElement('p');
      date.className = 'blog-modal-meta-date';
      date.insertAdjacentHTML('beforeend', item.details);
      meta.append(date);
    }

    const article = document.createElement('div');
    article.className = 'blog-modal-article';
    article.innerHTML = '<p class="blog-modal-loading">Loading…</p>';

    const scroll = document.createElement('div');
    scroll.className = 'blog-modal-scroll';
    scroll.append(meta, article);

    modal.append(scroll, closeBtn);
    overlay.append(modal);
    document.body.append(overlay);

    this.blogModalEl = overlay;
    this.blogModalTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');

    // Prevent body scroll while the overlay is open, matching the photo modal.
    document.documentElement.style.overflow = 'hidden';

    requestAnimationFrame(() => overlay.classList.add('visible'));

    // Close on backdrop click, close button, or Escape — clicking inside the
    // scrollable article itself must not close the overlay.
    closeBtn.addEventListener('click', () => this.closeBlogModal());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) this.closeBlogModal(); });

    this.blogModalKeyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.closeBlogModal();
    };
    document.addEventListener('keydown', this.blogModalKeyHandler);

    requestAnimationFrame(() => closeBtn.focus());

    fetch(item.markdownPath)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch ${item.markdownPath}: ${res.status}`);
        return res.text();
      })
      .then((markdown) => {
        article.innerHTML = marked.parse(markdown) as string;
      })
      .catch((error) => {
        console.error(`Failed to load blog post ${item.id}:`, error);
        article.innerHTML = '<p>Sorry, this post could not be loaded.</p>';
      });
  }

  private closeBlogModal(): void {
    if (!this.blogModalEl) return;
    const overlay = this.blogModalEl;
    this.blogModalEl = null;

    if (this.blogModalKeyHandler) {
      document.removeEventListener('keydown', this.blogModalKeyHandler);
      this.blogModalKeyHandler = null;
    }

    document.documentElement.style.overflow = '';

    this.blogModalTrigger?.setAttribute('aria-expanded', 'false');
    this.blogModalTrigger?.focus();
    this.blogModalTrigger = null;

    overlay.classList.remove('visible');
    overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
    window.setTimeout(() => overlay.remove(), TRANSITION_MS + 50);
  }

  /**
   * Create a tile that is permanently in the expanded state — used for
   * categories where tiles never collapse (Graphics, Mathematics).
   * No close button is added and no click handler is attached to the header.
   */
  private createStaticTile(item: GalleryItemDef): HTMLElement {
    const tile = document.createElement('div');
    tile.className = 'gallery-item expanded';
    if (item.model) tile.classList.add('has-model');
    tile.dataset.itemId = item.id;
    tile.dataset.static = 'true';
    this.applyModelCanvasStyleVars(tile, item);

    // The 3-D model (when present) fills the entire card as a full-bleed
    // background, sitting behind the header and details panel. Mounted first
    // so it paints below the rest of the (positioned, higher z-index) content.
    const modelMount = document.createElement('div');
    modelMount.className = 'gallery-item-model-mount';

    // Use a <div> instead of <button> — the header is decorative/non-interactive.
    const header = document.createElement('div');
    header.className = 'gallery-item-toggle gallery-item-static-header';

    const media = document.createElement('div');
    media.className = 'gallery-item-media';
    this.applyTileBackground(media, item);
    if (item.imageAlt) {
      media.setAttribute('role', 'img');
      media.setAttribute('aria-label', item.imageAlt);
    }

    const caption = document.createElement('div');
    caption.className = 'gallery-item-caption';
    caption.append(this.createTitleEl(item, 'h3', 'caption'));
    const desc = document.createElement('p');
    desc.textContent = item.description;
    caption.append(desc);

    header.append(media, caption);

    const body = document.createElement('div');
    body.className = 'gallery-item-body visible';

    const details = document.createElement('div');
    details.className = 'gallery-item-details';

    const subtext = document.createElement('p');
    subtext.className = 'gallery-item-subtext';
    subtext.textContent = item.description;

    details.append(this.createTitleEl(item, 'h2', 'details'), subtext);
    details.insertAdjacentHTML('beforeend', item.details);

    body.append(details);
    tile.append(modelMount, header, body);
    return tile;
  }

  // ============================================================================
  // Inline expand/collapse
  // ============================================================================

  private toggleItem(item: GalleryItemDef, tile: HTMLElement): void {
    if (this.isBusy) return;
    if (tile.classList.contains('expanded')) {
      this.collapseItem(tile);
      return;
    }
    this.expandItem(item, tile);
  }

  private expandItem(item: GalleryItemDef, tile: HTMLElement): void {
    this.isBusy = true;

    this.animateTileSize(tile, () => {
      tile.classList.add('expanded');
      tile.classList.toggle('has-model', Boolean(item.model));
    });

    const toggle = tile.querySelector<HTMLButtonElement>('.gallery-item-toggle');
    toggle?.setAttribute('aria-expanded', 'true');

    // The close button sits outside the animated body so it is clickable
    // immediately, unaffected by the body's reveal clip-path.
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'gallery-item-close';
    closeBtn.setAttribute('aria-label', 'Collapse');
    closeBtn.innerHTML =
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">' +
      '<line x1="6" y1="6" x2="18" y2="18"></line><line x1="18" y1="6" x2="6" y2="18"></line></svg>';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.collapseItem(tile);
    });

    const body = document.createElement('div');
    body.className = 'gallery-item-body';

    if (item.category === 'photography') {
      // ── Photography: full image at natural proportions below the 72px header ─────
      body.classList.add('gallery-item-photo-body');

      const img = document.createElement('img');
      img.className = 'photo-tile-image';
      img.src = item.imageSrc ?? '';
      img.alt = item.imageAlt ?? item.title;
      img.draggable = false;
      body.append(img);

      const footer = document.createElement('div');
      footer.className = 'photo-tile-footer';
      if (item.photoInfo) {
        const parts: string[] = [];
        if (item.photoInfo.location) parts.push(item.photoInfo.location);
        if (item.photoInfo.date) parts.push(item.photoInfo.date);
        if (item.photoInfo.camera) parts.push(item.photoInfo.camera);
        if (item.photoInfo.film) parts.push(item.photoInfo.film);
        const meta = document.createElement('p');
        meta.className = 'photo-info-meta';
        meta.textContent = parts.join('  ·  ');
        footer.append(meta);
      }
      body.append(footer);
    } else {
      // ── Standard: title, optional 3-D model, description + details ────────
      const modelMount = document.createElement('div');
      modelMount.className = 'gallery-item-model-mount';

      const details = document.createElement('div');
      details.className = 'gallery-item-details';

      const subtext = document.createElement('p');
      subtext.className = 'gallery-item-subtext';
      subtext.textContent = item.description;

      details.append(this.createTitleEl(item, 'h2', 'details'), subtext);
      details.insertAdjacentHTML('beforeend', item.details);

      body.append(modelMount, details);

      if (item.model) {
        const canvas = document.createElement('canvas');
        canvas.className = 'model-canvas';
        modelMount.appendChild(canvas);
        this.modelViewer = new ModelViewer(canvas);
        this.modelViewer.loadFromDef(item.model).catch((error) => {
          console.error(`Failed to load model for ${item.id}:`, error);
        });
      }
    }

    tile.append(closeBtn, body);
    tile.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    void body.offsetHeight; // force reflow so the reveal transition runs
    body.classList.add('visible');
    closeBtn.classList.add('visible');

    window.setTimeout(() => {
      // Only clear the busy flag / move focus if this item is still expanded —
      // it may have already been collapsed in the meantime.
      if (tile.classList.contains('expanded')) {
        this.isBusy = false;
        closeBtn.focus();
        tile.style.width = '';
        tile.style.height = '';
        tile.style.overflow = '';
        tile.style.transition = '';
        tile.style.transform = '';
      }
    }, TRANSITION_MS);
  }

  /**
   * Run a FLIP animation on all tiles in the grid so they animate smoothly
   * to their new positions when one of them expands/collapses.
   */
  private animateTileSize(tile: HTMLElement, applyClassChange: () => void): void {
    const tilesToAnimate = Array.from(this.gridEl.children) as HTMLElement[];
    const firstRects = new Map<HTMLElement, DOMRect>();

    for (const t of tilesToAnimate) {
      firstRects.set(t, t.getBoundingClientRect());
    }

    applyClassChange();

    const lastRects = new Map<HTMLElement, DOMRect>();
    for (const t of tilesToAnimate) {
      lastRects.set(t, t.getBoundingClientRect());
    }

    for (const t of tilesToAnimate) {
      const first = firstRects.get(t);
      const last = lastRects.get(t);
      if (!first || !last) continue;

      const dx = first.left - last.left;
      const dy = first.top - last.top;

      if (t === tile) {
        // the animating tile itself also changes size
        t.style.transition = 'none';
        t.style.width = `${first.width}px`;
        t.style.height = `${first.height}px`;
        t.style.transform = `translate(${dx}px, ${dy}px)`;
        
        void t.offsetHeight; // force reflow

        t.style.transition = `transform ${TRANSITION_MS}ms var(--transition-easing), width ${TRANSITION_MS}ms var(--transition-easing), height ${TRANSITION_MS}ms var(--transition-easing)`;
        t.style.width = `${last.width}px`;
        t.style.height = `${last.height}px`;
        t.style.transform = '';
      } else {
        // other tiles just change pos
        if (dx !== 0 || dy !== 0) {
          t.style.transition = 'none';
          t.style.transform = `translate(${dx}px, ${dy}px)`;
          
          void t.offsetHeight;
          
          t.style.transition = `transform ${TRANSITION_MS}ms var(--transition-easing)`;
          t.style.transform = '';
          
          setTimeout(() => {
            t.style.transition = '';
            t.style.transform = '';
          }, TRANSITION_MS);
        }
      }
    }
  }

  private collapseItem(tile: HTMLElement): void {
    if (!tile.classList.contains('expanded')) return;
    if (tile.dataset.static) return; // always-expanded tiles never collapse

    this.isBusy = true;

    const body = tile.querySelector<HTMLElement>('.gallery-item-body');
    const closeBtn = tile.querySelector<HTMLButtonElement>('.gallery-item-close');
    const toggle = tile.querySelector<HTMLButtonElement>('.gallery-item-toggle');

    // Only dispose if this specific tile had a model viewer
    if (tile.classList.contains('has-model') && this.modelViewer) {
      this.modelViewer.dispose();
      this.modelViewer = null; 
    }

    // --- Synchronized three-part collapse animation ---
    //
    // The root cause of the "jump": the toggle's CSS flex-basis transition
    // (72px → 100%) fires when 'expanded' is removed and pushes the body
    // downward, shifting visible content before it can animate out.
    //
    // Fix: drive all three pieces with the same duration + easing so that
    //   toggle (72px → collapsedH) + body (fullH → 0) == tile (expandedH → collapsedH)
    // at every animation frame — no overflow, no layout shift.

    // 1. Snapshot expanded measurements before any DOM change.
    //    Use the TILE's current height minus the toggle to get the VISIBLE body
    //    height — the body's natural content height can exceed the tile height
    //    (the tile normally has no overflow:hidden), so using bodyH directly
    //    would make toggle+body > tile at t=0, causing instant clipping.
    const tileFirstH = tile.getBoundingClientRect().height;
    const toggleH    = toggle?.getBoundingClientRect().height ?? 0;
    const bodyH      = tileFirstH - toggleH; // visible body area within the tile

    // 2. Temporarily remove the class to measure the collapsed tile height
    tile.classList.remove('expanded', 'has-model');
    const collapsedH = tile.getBoundingClientRect().height;
    tile.classList.add('expanded'); // restore for FLIP below

    // 3. Set initial inline states for toggle and body *before* FLIP runs.
    //    FLIP's internal void-offsetHeight reflow will lock these in as the
    //    transition start-values before the final targets are set.
    if (toggle) {
      toggle.style.transition = 'none';         // suppress CSS flex-basis transition
      toggle.style.flexBasis  = `${toggleH}px`; // pin at 72px (expanded value)
    }
    if (body) {
      body.style.height    = `${bodyH}px`;
      document.documentElement.style.overflow = 'hidden';
      // Pin clip-path open so .visible removal below has no visual effect
      body.style.clipPath  = 'inset(0 0 0% 0)';
    }

    // 4. FLIP all tiles — applyClassChange removes 'expanded' again; the internal
    //    void-offsetHeight locks in the toggle/body initial states set above.
    this.animateTileSize(tile, () => {
      tile.classList.remove('expanded', 'has-model');
    });

    // overflow:hidden on tile clips any sub-pixel overflow during animation
    tile.style.overflow = 'hidden';

    // 5. Set synchronized transitions and final targets.
    //    All three start at the next repaint frame, so they're perfectly in sync.
    if (toggle) {
      toggle.style.transition = `flex-basis ${TRANSITION_MS}ms cubic-bezier(0.65, 0, 0.35, 1)`;
      toggle.style.flexBasis  = `${collapsedH}px`;
    }
    if (body) {
      body.style.transition = `height ${TRANSITION_MS}ms cubic-bezier(0.65, 0, 0.35, 1), opacity ${TRANSITION_MS}ms cubic-bezier(0.65, 0, 0.35, 1)`;
      body.style.height     = '0';
      body.style.opacity    = '0';
    }

    toggle?.setAttribute('aria-expanded', 'false');
    toggle?.focus();

    body?.classList.remove('visible');
    closeBtn?.classList.remove('visible');

    window.setTimeout(() => {
      body?.remove();
      closeBtn?.remove();
      this.isBusy = false;
      tile.style.width      = '';
      tile.style.height     = '';
      tile.style.overflow   = '';
      document.documentElement.style.overflow = '';
      tile.style.transition = '';
      tile.style.transform  = '';
      if (toggle) {
        toggle.style.flexBasis  = '';
        toggle.style.transition = '';
      }
    }, TRANSITION_MS);
  }

  private onDocumentKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      const expandedTiles = Array.from(this.gridEl.querySelectorAll('.gallery-item.expanded')) as HTMLElement[];
      const lastExpanded = expandedTiles[expandedTiles.length - 1];
      if (lastExpanded) {
        this.collapseItem(lastExpanded);
      }
    }
  }
}
